import { useState } from "react";
import { t } from "@/lib/i18n/messages";
import { useAppContext } from "@/providers";
import type { PasswordChangeInput } from "@/types/user";

interface PasswordChangeFormProps {
  onSubmit: (data: PasswordChangeInput) => Promise<boolean>;
  loading: boolean;
}

export function PasswordChangeForm({ onSubmit, loading }: PasswordChangeFormProps) {
  const { locale } = useAppContext();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!currentPassword) {
      newErrors.currentPassword = t(locale, "requiredField");
    }
    if (!newPassword) {
      newErrors.newPassword = t(locale, "requiredField");
    } else if (newPassword.length < 8) {
      newErrors.newPassword = t(locale, "passwordMinLength");
    }
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t(locale, "passwordMismatch");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    const success = await onSubmit({
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: confirmPassword,
    });
    if (success) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
    }
  }

  const inputClass =
    "w-full px-4 py-2.5 bg-muted border-none rounded-xl focus:ring-2 focus:ring-primary transition-all placeholder:text-muted-foreground";

  return (
    <section className="bg-card p-6 rounded-lg shadow-sm border border-border">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-600">
          <span className="material-icons-outlined">lock_reset</span>
        </div>
        <h2 className="text-lg font-bold">{t(locale, "changePassword")}</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1.5">
            {t(locale, "currentPassword")} <span className="text-primary">*</span>
          </label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className={inputClass}
            placeholder={t(locale, "enterCurrentPassword")}
          />
          {errors.currentPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.currentPassword}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">
            {t(locale, "newPassword")} <span className="text-primary">*</span>
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={inputClass}
            placeholder={t(locale, "enterNewPassword")}
          />
          {errors.newPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">
            {t(locale, "confirmPassword")} <span className="text-primary">*</span>
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputClass}
            placeholder={t(locale, "reenterNewPassword")}
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
          )}
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full mt-2 bg-primary text-white py-3 rounded-xl font-bold shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? t(locale, "loading") : t(locale, "changePasswordBtn")}
        </button>
      </form>
    </section>
  );
}
